import { useState, useEffect } from 'react'
import { useAppDispatch, useIsAuthenticated, useUser } from '@/store/hooks'
import { loginUser, clearError } from '@/store/slices/authSlice'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { sessionService } from '@/services/sessionService'
import { useAuthStore } from '@/store/authStore'

const UserLoginPage = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const isAuthenticated = useIsAuthenticated()
  const user = useUser()
  const { login: loginZustand } = useAuthStore()

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user && (user.role === 'department_head' || user.role === 'front_worker' || user.role === 'staff' || user.role === 'committee_member')) {
      navigate('/app/staff/dashboard', { replace: true })
    }
  }, [isAuthenticated, user, navigate])

  // Clear any existing errors on component mount
  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      console.log('Starting staff login...')
      const resultAction = await dispatch(loginUser({
        email,
        password,
        loginType: 'staff'
      }))

      if (loginUser.fulfilled.match(resultAction)) {
        const { user, sessionId, token, refreshToken } = resultAction.payload
        console.log('Login successful, user:', user)

        // Check if user has staff/committee privileges
        if (user && (user.role === 'department_head' || user.role === 'front_worker' || user.role === 'staff' || user.role === 'committee_member')) {
          console.log('Staff/Committee user authenticated, starting session and navigating...')

          // Sync Zustand store with Redux state
          try {
            await loginZustand(email, password, 'staff')
          } catch (err) {
            // If Zustand login fails, manually set the state
            useAuthStore.setState({
              user,
              token,
              refreshToken,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }

          // Start session tracking
          if (sessionId) {
            sessionService.startSession(sessionId)
          }

          // Navigate to dashboard with replace to prevent going back to login
          navigate('/app/staff/dashboard', { replace: true })
        } else {
          console.error('User role not authorized for staff access:', user?.role)
          setError('Access denied. This login is for staff and committee members only.')
        }
      } else {
        // Handle login failure
        const errorMessage = resultAction.payload as string
        console.error('Staff login failed:', errorMessage)
        setError(errorMessage || 'Login failed. Please check your credentials.')
      }
    } catch (error: any) {
      console.error('Staff login error:', error)
      setError(error.message || 'Login failed. Please check your credentials.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
              <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Login
            </h2>
            <p className="text-gray-600 mb-6">
              Department Heads, Staff & Committee Members
            </p>
          </div>

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {(location.state as { passwordReset?: boolean } | null)?.passwordReset && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
                Password updated successfully. You can now log in.
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Work Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="staff@nfcrwa.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter your password"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link to="/forgot-password?type=staff" className="font-medium text-green-600 hover:text-green-500">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign in to Dashboard'}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-2">
            <Link 
              to="/" 
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              ← Back to Home
            </Link>
            <div className="text-xs text-gray-500">
              For member login, use <Link to="/login" className="text-blue-600 hover:text-blue-800">Member Login</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserLoginPage
